import { useId, useRef, useState } from 'react';
import {
  ImportError,
  MAX_LOCATION_NAME,
  buildExport,
  createId,
  exportFilename,
  parseExport,
  withDefaultLocation,
  withLocation,
  withRenamedLocation,
  withoutLocation,
  type LocalePreference,
  type ShelfLocation,
} from '@physical-movies/core';
import {
  useAuth,
  useCollection,
  useI18n,
  useTheme,
  useToast,
  type ThemePreference,
} from '../app/context';
import { Icon } from '../components/Icon';
import { PageHeader, Section } from '../components/Page';
import { Sheet } from '../components/Sheet';
import { Button, Field, IconButton, Segmented, TextInput } from '../components/ui';
import { downloadText } from '../lib/download';
import { useScrollToTop } from '../lib/useScrollRestoration';
import styles from './settings.module.css';

export function Settings() {
  useScrollToTop();
  const { t, locale, preference: languagePreference, setPreference: setLanguage } = useI18n();
  const { preference: themePreference, setPreference: setTheme } = useTheme();
  const { user, signOut } = useAuth();
  const collection = useCollection();
  const toast = useToast();

  const fileRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<{ message: string; failed: boolean } | null>(null);

  const exportCollection = () => {
    const payload = buildExport({
      movies: collection.movies,
      watches: collection.watches,
      wishlist: collection.wishlist,
      settings: collection.settings,
      appVersion: __APP_VERSION__,
    });
    downloadText(exportFilename(), JSON.stringify(payload, null, 2));
    toast.show(t('settings.exported'));
  };

  const importFile = async (file: File) => {
    setImporting(true);
    setResult(null);
    try {
      const parsed = parseExport(await file.text());
      const summary = await collection.importCollection(parsed);
      setResult({
        message: t('settings.imported', {
          movies: summary.movies,
          watches: summary.watches,
          wishlist: summary.wishlist,
        }),
        failed: false,
      });
    } catch (error) {
      const reason = error instanceof ImportError ? error.message : t('error.generic');
      setResult({ message: t('settings.importFailed', { reason }), failed: true });
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className={styles.page}>
      <PageHeader title={t('settings.title')} />

      <Section title={t('settings.appearance')}>
        <div className={styles.group}>
          <div className={styles.control}>
            <span className="eyebrow">{t('settings.theme')}</span>
            <Segmented<ThemePreference>
              label={t('settings.theme')}
              value={themePreference}
              onChange={setTheme}
              options={[
                { value: 'system', label: t('settings.theme.system') },
                { value: 'light', label: t('settings.theme.light') },
                { value: 'dark', label: t('settings.theme.dark') },
              ]}
            />
          </div>

          <div className={styles.control}>
            <span className="eyebrow">{t('settings.language')}</span>
            <Segmented<LocalePreference>
              label={t('settings.language')}
              value={languagePreference}
              onChange={setLanguage}
              options={[
                { value: 'system', label: t('settings.language.system') },
                { value: 'en', label: t('settings.language.en') },
                { value: 'de', label: t('settings.language.de') },
              ]}
            />
          </div>
        </div>
      </Section>

      <LocationSettings />

      <Section title={t('settings.data')}>
        <div className={styles.group}>
          <div className={styles.dataRow}>
            <Button block onClick={exportCollection}>
              <Icon name="download" size={16} />
              {t('settings.export')}
            </Button>
            <p className={styles.hint}>{t('settings.exportHint')}</p>
          </div>

          <div className={styles.dataRow}>
            <Button block disabled={importing} onClick={() => fileRef.current?.click()}>
              <Icon name="upload" size={16} />
              {importing ? t('settings.importing') : t('settings.import')}
            </Button>
            <p className={styles.hint}>{t('settings.importHint')}</p>
            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              className={styles.fileInput}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void importFile(file);
              }}
            />
            {result && (
              <p className={`${styles.result} ${result.failed ? styles.resultError : ''}`}>
                {result.message}
              </p>
            )}
          </div>
        </div>
      </Section>

      <Section title={t('settings.account')}>
        <div className={styles.group}>
          <div className={styles.account}>
            <span className={styles.email}>
              {t('settings.signedInAs', { email: user?.email ?? '—' })}
            </span>
            <Button onClick={() => void signOut()}>{t('common.signOut')}</Button>
          </div>
        </div>
      </Section>

      <Section title={t('settings.about')}>
        <div className={styles.group}>
          <div className={styles.about}>
            <div className={styles.aboutRow}>
              <span>{t('settings.version')}</span>
              <span className={styles.aboutValue}>{__APP_VERSION__}</span>
            </div>
            <div className={styles.aboutRow}>
              <span>{t('settings.language')}</span>
              <span className={styles.aboutValue}>{locale === 'de' ? 'Deutsch' : 'English'}</span>
            </div>
          </div>
          <p className={styles.credit}>{t('settings.tmdbCredit')}</p>
        </div>
      </Section>
    </div>
  );
}

/**
 * Where the discs are.
 *
 * The list is deliberately small: a name and which one is the default. A disc
 * that does not name a location counts as being at the default, so adding the
 * first location silently places everything already on the shelf rather than
 * asking about several hundred discs one at a time.
 */
function LocationSettings() {
  const { t } = useI18n();
  const collection = useCollection();
  const toast = useToast();
  const fieldId = useId();

  const [name, setName] = useState('');
  const [renaming, setRenaming] = useState<ShelfLocation | null>(null);
  const [removing, setRemoving] = useState<ShelfLocation | null>(null);
  const [busy, setBusy] = useState(false);

  const settings = collection.settings;

  const save = async (next: typeof settings) => {
    setBusy(true);
    try {
      await collection.saveSettings(next);
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setName('');
    await save(withLocation(settings, { id: createId(), name: trimmed }));
    toast.show(t('settings.locations.added', { name: trimmed }));
  };

  const confirmRemoval = async () => {
    if (!removing) return;
    const gone = removing;
    setRemoving(null);
    await save(withoutLocation(settings, gone.id));
    toast.show(t('settings.locations.removed', { name: gone.name }));
  };

  return (
    <Section title={t('settings.locations')}>
      <p className={styles.hint}>{t('settings.locations.hint')}</p>

      {settings.locations.length === 0 ? (
        <p className={styles.hint}>{t('settings.locations.empty')}</p>
      ) : (
        <ul className={styles.locationList}>
          {settings.locations.map((location) => {
            const isDefault = location.id === settings.defaultLocationId;
            return (
              <li key={location.id} className={styles.locationRow}>
                <button
                  type="button"
                  className={styles.locationName}
                  onClick={() => setRenaming(location)}
                  aria-label={t('settings.locations.rename', { name: location.name })}
                >
                  <span>{location.name}</span>
                  {isDefault && (
                    <span className={styles.defaultTag}>{t('settings.locations.default')}</span>
                  )}
                </button>
                {!isDefault && (
                  <IconButton
                    name="check"
                    label={t('settings.locations.makeDefault')}
                    disabled={busy}
                    onClick={() => void save(withDefaultLocation(settings, location.id))}
                  />
                )}
                <IconButton
                  name="trash"
                  label={t('settings.locations.remove', { name: location.name })}
                  disabled={busy}
                  onClick={() => setRemoving(location)}
                />
              </li>
            );
          })}
        </ul>
      )}

      {settings.defaultLocationId !== null && (
        <p className={styles.hint}>{t('settings.locations.defaultHint')}</p>
      )}

      <div className={styles.locationAdd}>
        <Field label={t('settings.locations.add')} htmlFor={`${fieldId}-name`}>
          <TextInput
            id={`${fieldId}-name`}
            value={name}
            maxLength={MAX_LOCATION_NAME}
            placeholder={t('settings.locations.namePlaceholder')}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') void add();
            }}
          />
        </Field>
        <Button disabled={busy || name.trim() === ''} onClick={() => void add()}>
          {t('settings.locations.save')}
        </Button>
      </div>

      <RenameSheet
        location={renaming}
        onClose={() => setRenaming(null)}
        onRename={(id, next) => {
          setRenaming(null);
          void save(withRenamedLocation(settings, id, next));
        }}
      />

      <Sheet
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title={t('settings.locations.deleteTitle', { name: removing?.name ?? '' })}
        closeLabel={t('common.close')}
        footer={
          <>
            <Button onClick={() => setRemoving(null)}>{t('common.cancel')}</Button>
            <Button variant="danger" onClick={() => void confirmRemoval()}>
              {t('settings.locations.deleteAction')}
            </Button>
          </>
        }
      >
        <p className={styles.hint}>{t('settings.locations.deleteBody')}</p>
      </Sheet>
    </Section>
  );
}

/** Keyed on the location so the field starts from the current name each time. */
function RenameSheet({
  location,
  onClose,
  onRename,
}: {
  location: ShelfLocation | null;
  onClose(): void;
  onRename(id: string, name: string): void;
}) {
  const { t } = useI18n();

  return (
    <Sheet
      open={location !== null}
      onClose={onClose}
      title={t('settings.locations.rename', { name: location?.name ?? '' })}
      closeLabel={t('common.close')}
    >
      {location && (
        <RenameForm key={location.id} location={location} onRename={onRename} onCancel={onClose} />
      )}
    </Sheet>
  );
}

function RenameForm({
  location,
  onRename,
  onCancel,
}: {
  location: ShelfLocation;
  onRename(id: string, name: string): void;
  onCancel(): void;
}) {
  const { t } = useI18n();
  const fieldId = useId();
  const [draft, setDraft] = useState(location.name);

  return (
    <div className={styles.locationAdd}>
      <Field label={t('settings.locations')} htmlFor={`${fieldId}-rename`}>
        <TextInput
          id={`${fieldId}-rename`}
          value={draft}
          maxLength={MAX_LOCATION_NAME}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && draft.trim() !== '') onRename(location.id, draft);
          }}
        />
      </Field>
      <Button
        variant="primary"
        disabled={draft.trim() === ''}
        onClick={() => onRename(location.id, draft)}
      >
        {t('common.save')}
      </Button>
      <Button variant="ghost" onClick={onCancel}>
        {t('common.cancel')}
      </Button>
    </div>
  );
}
